import React, { useState } from 'react';
import {
  X, BookOpen, GraduationCap, CheckCircle,
  Lock, Gem, ExternalLink, FolderGit2, Star, Plus, Coins, Eye
} from 'lucide-react';
import { useGame } from '../context/GameContext';

// IMPORT DATA
import { SHOP_ITEMS } from '../data/shopData';

const GemShop = ({ onClose }) => {
  const { gems, setGems, inventory = [], setInventory, balance, setBalance } = useGame();

  // State for the quantity of gems to buy
  const [buyAmount, setBuyAmount] = useState(1);

  const GEM_PRICE = 100000;

  const handlePurchase = (item) => {
    if (gems < item.cost) return;
    if (inventory.includes(item.id)) return;

    setGems(prev => prev - item.cost);
    setInventory(prev => [...prev, item.id]);
  };

  const handleAccess = (link) => {
    if (link) window.open(link, '_blank');
  };

  // --- BUY GEMS LOGIC ---
  const handleBuyGems = () => {
    const qty = parseInt(buyAmount);
    if (!qty || qty < 1) return;

    const totalCost = qty * GEM_PRICE;

    if (balance >= totalCost) {
      if (window.confirm(`Buy ${qty} Gem${qty > 1 ? 's' : ''} for $${totalCost.toLocaleString()}?`)) {
        setBalance(prev => prev - totalCost);
        setGems(prev => prev + qty);
        setBuyAmount(1); // Reset to 1 after purchase
      }
    } else {
      alert(`Insufficient Funds! You need $${totalCost.toLocaleString()} for ${qty} gems.`);
    }
  };

  // Helper to dynamically render icons based on category
  const getIcon = (category, isOwned) => {
    if (category === 'Course') {
      return <BookOpen size={24} className={isOwned ? "text-cyan-400" : "text-yellow-400"} />;
    }
    return <FolderGit2 size={24} className={isOwned ? "text-cyan-400" : "text-blue-400"} />;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex justify-center items-center p-4 animate-in fade-in duration-200">

      {/* Main Container with Intensified Cyan Glow (Matching Hardware Shop) */}
      <div className="bg-[#0a0a0f] border-2 border-cyan-400/60 w-full max-w-6xl h-[85vh] rounded-3xl flex flex-col overflow-hidden shadow-[0_0_80px_rgba(6,182,212,0.3)] relative">

        {/* HEADER */}
        <div className="p-6 border-b-2 border-cyan-500/30 flex flex-col lg:flex-row justify-between items-center bg-gradient-to-r from-cyan-950/50 to-[#0a0a0f] gap-4">
          <div className="text-center lg:text-left">
            <h2 className="text-2xl font-black text-white uppercase tracking-widest flex items-center justify-center lg:justify-start gap-3 drop-shadow-[0_0_15px_rgba(6,182,212,0.6)]">
              <GraduationCap className="text-cyan-400" size={32} /> DATASENSE GEM Store
            </h2>
            <p className="text-cyan-200/60 text-xs font-mono mt-1 font-bold">Unlock real-world Projects & Courses with Gems.</p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">

            {/* BALANCE DISPLAY (Matching Hardware Shop Style) */}
            <div className="bg-black/80 px-4 py-2 rounded-xl border-2 border-yellow-500/40 text-yellow-400 font-mono text-xs font-bold shadow-[0_0_25px_rgba(234,179,8,0.2)] flex items-center gap-2">
              <Coins size={14} /> ${balance.toLocaleString()}
            </div>

            {/* GEM BUYING CONTROLS (Updated Theme) */}
            <div className="flex items-center gap-2 bg-black/60 p-1.5 rounded-xl border border-cyan-500/30">
              <input
                type="number"
                min="1"
                max="99"
                value={buyAmount}
                onChange={(e) => setBuyAmount(e.target.value)}
                className="w-12 bg-[#0a0a0f] text-cyan-200 text-center font-bold text-xs py-1.5 rounded-lg border border-cyan-500/20 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(6,182,212,0.2)]"
              />
              <button
                onClick={handleBuyGems}
                disabled={balance < (buyAmount * GEM_PRICE)}
                className="bg-cyan-600/20 hover:bg-cyan-500 hover:text-black border border-cyan-500/50 text-cyan-400 px-3 py-1.5 rounded-lg font-bold text-xs uppercase tracking-wider flex items-center gap-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_10px_rgba(6,182,212,0.1)] hover:shadow-[0_0_20px_rgba(6,182,212,0.4)]"
                title={`Cost: $${(buyAmount * GEM_PRICE).toLocaleString()}`}
              >
                <Plus size={12} /> Buy
              </button>
            </div>

            {/* GEM COUNT */}
            <div className="bg-fuchsia-900/20 px-4 py-2 rounded-xl border border-fuchsia-500/50 text-fuchsia-300 font-mono font-bold flex items-center gap-2 shadow-[0_0_15px_rgba(236,72,153,0.3)]">
              <Gem size={16} className="fill-fuchsia-400/20" /> {gems}
            </div>

            <button onClick={onClose} className="bg-slate-900/80 hover:bg-red-900/80 p-2 rounded-full transition-all border-2 border-slate-700 hover:border-red-500 text-slate-400 hover:text-white hover:shadow-[0_0_20px_rgba(239,68,68,0.4)]">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-hide bg-[#050505] relative">
          {/* Subtle background glow grid overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#06b6d410_1px,transparent_1px),linear-gradient(to_bottom,#06b6d410_1px,transparent_1px)] bg-[size:2rem_2rem] pointer-events-none"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">

            {/* MAPPING THROUGH IMPORTED DATA */}
            {SHOP_ITEMS.map((item) => {

              const isOwned = (inventory || []).includes(item.id);
              const canAfford = gems >= item.cost;

              return (
                <div
                  key={item.id}
                  className={`
                    relative p-5 rounded-2xl border-2 transition-all group duration-300 flex flex-col justify-between min-h-[220px]
                    ${item.highlight && !isOwned ? 'border-yellow-500/50 bg-yellow-900/10 shadow-[0_0_20px_rgba(234,179,8,0.1)]' : ''}
                    ${isOwned
                      ? 'bg-cyan-950/40 border-cyan-400 shadow-[0_0_35px_rgba(6,182,212,0.3)]'
                      : 'bg-[#0a0a0f] border-cyan-500/10 hover:border-cyan-400/80 hover:bg-cyan-950/20 hover:shadow-[0_0_25px_rgba(6,182,212,0.3)]'}
                  `}
                >
                  {/* Badge for High Value Items */}
                  {item.highlight && !isOwned && (
                    <div className="absolute -top-3 -right-3 bg-yellow-500 text-black text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-wider flex items-center gap-1 shadow-[0_0_15px_rgba(234,179,8,0.6)]">
                      <Star size={10} fill="black" /> Featured
                    </div>
                  )}

                  <div>
                    <div className="flex justify-between items-start mb-4">
                      <div className={`p-3 rounded-xl border transition-all ${isOwned ? 'bg-cyan-500 text-black border-cyan-400' : 'bg-black/60 text-cyan-600/70 border-cyan-500/20 group-hover:text-cyan-400'}`}>
                        {/* Dynamic Icon Call */}
                        {getIcon(item.category, isOwned)}
                      </div>
                      {isOwned && <CheckCircle className="text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.8)]" size={24} />}
                    </div>

                    <div className="mb-2">
                      <span className={`text-[10px] uppercase tracking-widest font-black ${item.category === 'Course' ? 'text-yellow-500' : 'text-blue-400'}`}>
                        {item.category}
                      </span>
                    </div>

                    <h3 className={`font-black text-lg leading-tight mb-2 uppercase tracking-wide ${isOwned ? 'text-white drop-shadow-[0_0_5px_rgba(255,255,255,0.5)]' : 'text-white group-hover:text-cyan-100'}`}>
                      {item.title}
                    </h3>
                    <p className="text-xs text-slate-400 leading-relaxed font-mono group-hover:text-slate-300">
                      {item.subtitle}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-cyan-500/10 group-hover:border-cyan-500/30 flex flex-col gap-2">
                    {isOwned ? (
                      <button
                        onClick={() => handleAccess(item.lockedLink)}
                        className="w-full py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(6,182,212,0.4)] hover:shadow-[0_0_25px_rgba(6,182,212,0.6)] transition-all"
                      >
                        Access Full Content <ExternalLink size={14} />
                      </button>
                    ) : (
                      <>
                        {/* --- OPEN LINK BUTTON (FREE PREVIEW) --- */}
                        <button
                          onClick={() => handleAccess(item.openLink)}
                          className="w-full py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-cyan-400 border border-cyan-500/30 hover:border-cyan-400 font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all"
                        >
                          <Eye size={14} /> Preview Project
                        </button>

                        {/* --- UNLOCK BUTTON (PAID) --- */}
                        <button
                          onClick={() => handlePurchase(item)}
                          disabled={!canAfford}
                          className={`
                                    w-full py-3 rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all border-2
                                    ${canAfford
                              ? 'bg-fuchsia-600 hover:bg-fuchsia-500 border-fuchsia-500 text-white shadow-[0_0_15px_rgba(236,72,153,0.4)] hover:shadow-[0_0_25px_rgba(236,72,153,0.6)]'
                              : 'bg-slate-900/50 border-slate-700 text-slate-600 cursor-not-allowed'}
                                `}
                        >
                          {!canAfford && <Lock size={12} />}
                          <Gem size={12} className={canAfford ? "fill-white/20" : ""} />
                          Unlock Free Access ({item.cost})
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

export default GemShop;